import { useState } from 'react';
import { Plus, Pencil, Trash2, Wallet, Building2, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SectionCard } from './shared/SectionCard';
import { CajaEditorModal } from './cajas/CajaEditorModal';
import { useCajas } from '../hooks/useCajas';
import { useAlert } from '@/hooks/useAlert';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { SECTION_LABEL_CLASS } from '../constants/ui';
import { formatAmount } from '../utils/formatters';
import type { CuentaBancaria, CreateCajaPayload } from '../types';

export function CajasPage() {
  const { cajas, isLoading, createCaja, isCreating, updateCaja, isUpdating, deleteCaja, isDeleting } = useCajas();
  const { successAlert, errorAlert, questionAlert } = useAlert();
  const { canCreateContabilidad, canEditContabilidad, canDeleteContabilidad } = useAuthorization();
  const hasRowActions = canEditContabilidad || canDeleteContabilidad;

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CuentaBancaria | null>(null);

  const isMutating = isCreating || isUpdating || isDeleting;

  function handleAdd() {
    setEditing(null);
    setModalOpen(true);
  }

  function handleEdit(caja: CuentaBancaria) {
    setEditing(caja);
    setModalOpen(true);
  }

  async function handleDelete(caja: CuentaBancaria) {
    const { isConfirmed } = await questionAlert({
      title: `¿Eliminar "${caja.nombre}"?`,
      text: 'Esta acción no se puede deshacer. Solo se puede eliminar una caja sin movimientos.',
      confirmButtonText: 'Sí, eliminar',
    });
    if (!isConfirmed) return;

    try {
      await deleteCaja(caja.id);
      successAlert({ title: 'Caja eliminada', message: `"${caja.nombre}" fue eliminada.` });
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? undefined;
      errorAlert({ title: 'Error al eliminar', message: msg, exception: msg ? undefined : err });
    }
  }

  async function handleSubmit(payload: CreateCajaPayload) {
    try {
      if (editing) {
        await updateCaja({ id: editing.id, payload });
        successAlert({ title: 'Caja actualizada', message: `"${payload.nombre}" fue actualizada.` });
      } else {
        await createCaja(payload);
        successAlert({ title: 'Caja creada', message: `"${payload.nombre}" fue creada.` });
      }
      setModalOpen(false);
    } catch (err) {
      errorAlert({ title: editing ? 'Error al actualizar' : 'Error al crear', exception: err });
    }
  }

  if (isLoading) {
    return <div className="p-5 text-sm text-muted-foreground">Cargando...</div>;
  }

  return (
    <>
      <div className="space-y-5 p-2 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-gray-900">Cajas</h1>
          {canCreateContabilidad && (
            <Button size="sm" className="gap-2 rounded-full" onClick={handleAdd} disabled={isMutating}>
              <Plus className="size-4" /> Nueva Caja
            </Button>
          )}
        </div>

        <SectionCard className="overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
            <Wallet className="size-4 text-muted-foreground" />
            <p className={SECTION_LABEL_CLASS}>Gestión de Cajas y Cuentas</p>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className={SECTION_LABEL_CLASS}>Nombre</TableHead>
                  <TableHead className={SECTION_LABEL_CLASS}>Tipo</TableHead>
                  <TableHead className={SECTION_LABEL_CLASS}>Moneda</TableHead>
                  <TableHead className={SECTION_LABEL_CLASS}>Saldo Inicial</TableHead>
                  <TableHead className={SECTION_LABEL_CLASS}>Saldo Actual</TableHead>
                  {hasRowActions && <TableHead className={SECTION_LABEL_CLASS}>Acciones</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {cajas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={hasRowActions ? 6 : 5} className="text-center text-muted-foreground py-10">
                      No hay cajas registradas. Creá la primera.
                    </TableCell>
                  </TableRow>
                )}
                {cajas.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {c.tipo === 'banco' ? (
                          <Building2 className="size-4 text-blue-500 shrink-0" />
                        ) : (
                          <Banknote className="size-4 text-slate-500 shrink-0" />
                        )}
                        {c.nombre}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        {c.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium">{c.moneda}</TableCell>
                    <TableCell className="text-sm tabular-nums text-muted-foreground">
                      {c.moneda} $ {formatAmount(c.saldoInicial ?? 0, c.moneda)}
                    </TableCell>
                    <TableCell>
                      <span className={`text-sm font-semibold tabular-nums ${c.saldo >= 0 ? 'text-blue-600' : 'text-slate-600'}`}>
                        {c.moneda} $ {formatAmount(c.saldo, c.moneda)}
                      </span>
                    </TableCell>
                    {hasRowActions && (
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {canEditContabilidad && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8 text-muted-foreground hover:text-primary"
                              onClick={() => handleEdit(c)}
                              disabled={isMutating}
                              title="Editar"
                            >
                              <Pencil className="size-3.5" />
                            </Button>
                          )}
                          {canDeleteContabilidad && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-8 text-muted-foreground hover:text-gray-900"
                              onClick={() => handleDelete(c)}
                              disabled={isMutating}
                              title="Eliminar"
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionCard>
      </div>

      <CajaEditorModal
        open={modalOpen}
        caja={editing}
        isSubmitting={isCreating || isUpdating}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
